import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Wrench, Clock, Pencil } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { useBalk } from "@/lib/balkState";
import { useUnsavedGuard, DiscardDialog } from "@/components/useUnsavedGuard";
import { Plaatkop } from "@/components/Plaatkop";
import { PriorityBadge, FaultStatusBadge, FAULT_STATUS_LABELS, formatNlDate } from "@/components/Badges";
import { MediaGallery } from "@/components/MediaGallery";
import { MediaUpload } from "@/components/MediaUpload";
import { ErrorState } from "@/components/ListState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

// Allowed next statuses offered as buttons.
const NEXT_STATUSES = [
  "in_behandeling", "wacht_op_onderdelen", "gemeld_bij_verhuurder", "opgelost", "afgewezen",
];

const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <div className="text-data uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-body text-foreground">{value}</div>
    </div>
  );
};

export default function StoringDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  // The strip can change on any of these writes, so it is refreshed explicitly
  // rather than waiting up to 60s for the poll.
  const { refresh: refreshBalk } = useBalk();
  const [f, setF] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [location, setLocation] = useState(null);
  const [ruimtes, setRuimtes] = useState([]);
  const [savingMedia, setSavingMedia] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // status dialog
  const [statusDlg, setStatusDlg] = useState(null); // target status
  const [note, setNote] = useState("");
  const [setAssetStatus, setSetAssetStatus] = useState("__none__");
  const [busy, setBusy] = useState(false);
  // koppel-aan-bestaande dialog (round 26 §2.2)
  const [linkDlg, setLinkDlg] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");
  const [linkableBonnen, setLinkableBonnen] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  // edit fault dialog
  const [editDlg, setEditDlg] = useState(false);
  const EDIT_EMPTY = { asset_id: "__none__", ruimte_id: "__none__", spoor: "__none__", priority: "P3", description: "" };
  const [editForm, setEditForm] = useState(EDIT_EMPTY);
  const [editSnapshot, setEditSnapshot] = useState("");
  const editGuard = useUnsavedGuard(JSON.stringify(editForm) !== editSnapshot, () => setEditDlg(false));
  const statusGuard = useUnsavedGuard(note !== "" || setAssetStatus !== "__none__", () => setStatusDlg(null));

  const load = async () => {
    const { data } = await api.get(`/faults/${id}`);
    setF(data);
    if (data.location_id) {
      try {
        const loc = await api.get(`/locations/${data.location_id}`);
        setLocation(loc.data);
      } catch { /* ignore */ }
    }
    return data;
  };
  const reload = async () => {
    setLoading(true); setError("");
    try {
      const data = await load();
      const [asg, loc, rui, ass] = await Promise.all([
        api.get("/users/assignees"),
        api.get("/locations"),
        api.get(`/ruimtes?location_id=${data.location_id || ""}`),
        api.get(`/assets?location_id=${data.location_id || ""}`),
      ]);
      setAssignees(asg.data);
      setLocations(loc.data);
      setRuimtes(rui.data);
      setAssets(ass.data);
    } catch (e) {
      setError(getErrorMessage(e, "Storing niet gevonden."));
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const isClosed = f && ["opgelost", "afgewezen"].includes(f.status);
  const faultAssets = useMemo(
    () => assets.filter((a) => a.location_id === (f?.location_id)),
    [assets, f]
  );
  const faultRuimtes = useMemo(
    () => ruimtes.filter((r) => r.location_id === (f?.location_id)),
    [ruimtes, f]
  );
  // Rule 42: a storing with a reparatie freezes only the ASSET. Ruimte and spoor
  // stay correctable, because "hij staat op spoor 2, niet 3" is exactly the kind
  // of correction the man in the yard makes afterwards.
  const editRuimte = ruimtes.find((r) => r.id === editForm.ruimte_id);
  const editSporen = editRuimte?.kind === "washal" ? (editRuimte.sporen || []) : [];

  const openEdit = () => {
    const ef = {
      asset_id: f.asset_id || "__none__",
      ruimte_id: f.ruimte_id || "__none__",
      spoor: f.spoor || "__none__",
      priority: f.priority || "P3",
      description: f.description || "",
    };
    setEditForm(ef);
    setEditSnapshot(JSON.stringify(ef));
    setEditDlg(true);
  };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/faults/${id}`, {
        asset_id: editForm.asset_id === "__none__" ? null : editForm.asset_id,
        ruimte_id: editForm.ruimte_id === "__none__" ? null : editForm.ruimte_id,
        spoor: editForm.spoor === "__none__" ? null : editForm.spoor,
        priority: editForm.priority,
        description: editForm.description,
      });
      toast.success("Storing bijgewerkt");
      setEditDlg(false);
      await load();
      refreshBalk();
    } catch (e) { toast.error(getErrorMessage(e, "Bijwerken mislukt.")); }
    finally { setBusy(false); }
  };

  // Quick add photo to an existing fault (auto-saves).
  const saveMedia = async (media) => {
    setSavingMedia(true);
    try {
      await api.put(`/faults/${id}`, { media });
      setF((prev) => ({ ...prev, media }));
    } catch (e) { toast.error(getErrorMessage(e, "Foto opslaan mislukt.")); }
    finally { setSavingMedia(false); }
  };

  // §2.2: link a fault to a werkbon that already exists. Show open bonnen at
  // the same location first — "one machine can throw three faults that are
  // one job". Linking is undoable on the werkbon side.
  const openLinkDlg = async () => {
    setLinkDlg(true); setLinkSearch(""); setLinkLoading(true);
    try {
      const { data } = await api.get(`/werkbonnen?location_id=${f.location_id || ""}&open_only=true`);
      setLinkableBonnen(data);
    } catch (e) { toast.error(getErrorMessage(e, "Werkbonnen laden mislukt.")); }
    finally { setLinkLoading(false); }
  };
  const doLink = async (bonId) => {
    setBusy(true);
    try {
      await api.post(`/werkbonnen/${bonId}/link-fault`, { fault_id: id });
      toast.success("Storing gekoppeld aan werkbon");
      setLinkDlg(false);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Koppelen mislukt.")); }
    finally { setBusy(false); }
  };

  const applyStatus = async () => {
    setBusy(true);
    try {
      await api.post(`/faults/${id}/status`, {
        status: statusDlg,
        note,
        set_asset_status: setAssetStatus === "__none__" ? null : setAssetStatus,
      });
      toast.success("Status bijgewerkt");
      setStatusDlg(null); setNote(""); setSetAssetStatus("__none__");
      await load();
      refreshBalk();
    } catch (e) { toast.error(getErrorMessage(e, "Bijwerken mislukt.")); }
    finally { setBusy(false); }
  };

  const assign = async (userId) => {
    try {
      await api.post(`/faults/${id}/assign`, { assigned_to: userId === "__none__" ? null : userId });
      toast.success("Toewijzing bijgewerkt");
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Toewijzen mislukt.")); }
  };

  if (loading) {
    return (
      <div className="page-body space-y-4" data-testid="fault-loading">
        <Skeleton className="h-9 w-48" />
        <Card className="p-5"><Skeleton className="h-40 w-full" /></Card>
      </div>
    );
  }
  if (error || !f)
    return (
      <div className="page-body space-y-4">
        <Button variant="ghost" onClick={() => navigate("/storingen")}><ArrowLeft size={18} className="mr-2" /> Terug</Button>
        <ErrorState testid="fault-load-error" message={error || "Storing niet gevonden."} onRetry={reload} />
      </div>
    );

  const downtimeText = (() => {
    if (!f.downtime_start) return "";
    const start = new Date(f.downtime_start);
    const end = f.downtime_end ? new Date(f.downtime_end) : new Date();
    const hours = Math.max(0, (end - start) / 36e5);
    const label = hours >= 24 ? `${(hours / 24).toFixed(1)} dagen` : `${hours.toFixed(1)} uur`;
    return f.downtime_end ? `${label} (afgesloten)` : `${label} (loopt)`;
  })();

  return (
    <div className="page">
      <Plaatkop
        code={f.fault_number}
        srTitle={f.description || "Storing"}
        srTestid="fault-detail-title"
        subtitle={[f.location_name, f.ruimte_name, f.spoor, f.asset_code].filter(Boolean).join(" · ")}
        collapsibleFields
        backSlot={
          <Button variant="ghost" size="icon" data-testid="fault-back-button" className="-ml-2 text-white hover:bg-plaat-lijn hover:text-white" onClick={() => navigate("/storingen")}>
            <ArrowLeft size={18} />
          </Button>
        }
        fields={[
          { label: "Asset", value: f.asset_code, mono: true },
          { label: "Machine", value: f.asset_label },
          { label: "Locatie", value: f.location_name },
          { label: "Ruimte", value: f.ruimte_name },
          { label: "Spoor", value: f.spoor },
          { label: "Gemeld", value: formatNlDate((f.created_at || "").slice(0, 10)), mono: true },
          { label: "Downtime", value: downtimeText },
        ]}
        actions={
          canWrite() && !isClosed && (
            <Button variant="secondary" size="sm" data-testid="fault-edit-button" onClick={openEdit}>
              <Pencil size={18} className="mr-1.5" /> Bewerken
            </Button>
          )
        }
      />

      <div className="page-body space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <PriorityBadge priority={f.priority} /><FaultStatusBadge status={f.status} />
      </div>

      <Card className="p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Omschrijving" value={f.description} />
          <Field label="Asset" value={f.asset_code ? `${f.asset_code} — ${f.asset_label || ""}` : ""} />
          <Field label="Gemeld door" value={[f.reporter?.name, f.reporter?.source && `(${f.reporter.source})`].filter(Boolean).join(" ")} />
          <Field label="Gemeld op" value={formatNlDate((f.created_at || "").slice(0, 10))} />
          <div>
            <div className="text-data uppercase tracking-wide text-muted-foreground">Downtime</div>
            <div className="flex items-center gap-1 text-body text-foreground"><Clock size={14} /> {downtimeText}</div>
          </div>
          {f.werkbon_id && (
            <div>
              <div className="text-data uppercase tracking-wide text-muted-foreground">Werkbon</div>
              <button className="text-body text-primary hover:underline" data-testid="fault-werkbon-link" onClick={() => navigate(`/werkbonnen/${f.werkbon_id}`)}>Gekoppelde werkbon bekijken</button>
            </div>
          )}
          {f.repair_id && (
            <div>
              <div className="text-data uppercase tracking-wide text-muted-foreground">Reparatie</div>
              <button className="text-body text-primary hover:underline" onClick={() => f.asset_id && navigate(`/assets/${f.asset_id}`)}>Gekoppelde reparatie bekijken</button>
            </div>
          )}
        </div>
        <div className="mt-4">
          <Label className="mb-2 block">Media</Label>
          {f.media?.length > 0 ? <MediaGallery media={f.media} /> : <p className="text-body text-muted-foreground">Nog geen foto's.</p>}
          {canWrite() && (
            <div className="mt-3" data-testid="fault-media-add">
              <MediaUpload value={f.media || []} onChange={saveMedia} apiClient={api} uploadPath="/storage/upload" disabled={savingMedia} confirmRemove />
            </div>
          )}
        </div>
      </Card>

      {/* Actions */}
      {canWrite() && (
        <Card className="p-5">
          <h2 className="mb-3 text-body font-semibold text-foreground">Acties</h2>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] space-y-1.5">
              <Label>Toewijzen aan</Label>
              <Select value={f.assigned_to || "__none__"} onValueChange={assign}>
                <SelectTrigger data-testid="fault-assign-select"><SelectValue placeholder="Niemand" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Niet toegewezen</SelectItem>
                  {assignees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!f.werkbon_id && (
              <>
                <Button variant="secondary" data-testid="fault-werkbon-maken-button" onClick={() => navigate(`/werkbonnen?new=1&fault=${f.id}&loc=${f.location_id || ""}&ruimte=${f.ruimte_id || ""}&asset=${f.asset_id || ""}&desc=${encodeURIComponent(f.description || "")}`)}>
                  <Wrench size={18} className="mr-2" /> Werkbon maken
                </Button>
                <Button variant="outline" data-testid="fault-werkbon-koppelen-button" onClick={openLinkDlg}>
                  Koppelen aan bestaande werkbon
                </Button>
              </>
            )}
          </div>

          <Separator className="my-4" />
          <Label className="mb-2 block">Status wijzigen</Label>
          <div className="flex flex-wrap gap-2">
            {NEXT_STATUSES.filter((s) => s !== f.status).map((s) => (
              <Button
                key={s}
                size="sm"
                variant="outline"
                data-testid={`fault-status-btn-${s}`}
                onClick={() => { setStatusDlg(s); setNote(""); setSetAssetStatus("__none__"); }}
              >
                {FAULT_STATUS_LABELS[s]}
              </Button>
            ))}
          </div>
        </Card>
      )}

      {/* History */}
      <Card className="p-5">
        <h2 className="mb-3 text-body font-semibold text-foreground">Statusgeschiedenis</h2>
        <ol className="space-y-3" data-testid="fault-status-history">
          {(f.status_history || []).map((h, i) => (
            <li key={i} className="flex gap-3">
              <div className="mt-1 h-2 w-2 shrink-0 rounded bg-primary" />
              <div>
                <div className="text-body font-medium text-foreground">{FAULT_STATUS_LABELS[h.status] || h.status}</div>
                <div className="text-data text-muted-foreground">{new Date(h.at).toLocaleString("nl-NL")}{h.note ? ` — ${h.note}` : ""}</div>
              </div>
            </li>
          ))}
        </ol>
      </Card>

      {/* Status change dialog */}
      <Dialog open={!!statusDlg} onOpenChange={(o) => !o && statusGuard.requestClose(false)}>
        <DialogContent srDescription="Wijzig de status van deze storing en leg eventueel een notitie vast.">
          <DiscardDialog guard={statusGuard} />
          <DialogHeader><DialogTitle>Status: {statusDlg && FAULT_STATUS_LABELS[statusDlg]}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{statusDlg === "gemeld_bij_verhuurder" ? "Contactmoment / notitie (wordt vastgelegd als bewijs)" : "Notitie (optioneel)"}</Label>
              <Textarea data-testid="fault-status-note" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            {f.asset_id && (
              <div className="space-y-1.5">
                <Label>Assetstatus meebewegen (optioneel)</Label>
                <Select value={setAssetStatus} onValueChange={setSetAssetStatus}>
                  <SelectTrigger data-testid="fault-set-asset-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Niet wijzigen</SelectItem>
                    <SelectItem value="in_reparatie">In reparatie</SelectItem>
                    <SelectItem value="defect">Defect</SelectItem>
                    <SelectItem value="actief">Actief</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => statusGuard.requestClose(false)}>Annuleren</Button>
            <Button data-testid="fault-status-confirm" onClick={applyStatus} disabled={busy}>{busy ? "Bezig..." : "Bevestigen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit fault dialog (open faults only) */}
      <Dialog open={editDlg} onOpenChange={editGuard.requestClose}>
        <DialogContent srDescription="Pas de gegevens van deze storing aan.">
          <DiscardDialog guard={editGuard} />
          <DialogHeader><DialogTitle>Storing bewerken</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Omschrijving</Label>
              <Textarea data-testid="fault-edit-description" value={editForm.description} onChange={(e) => setEditForm((r) => ({ ...r, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Prioriteit</Label>
                <Select value={editForm.priority} onValueChange={(v) => setEditForm((r) => ({ ...r, priority: v }))}>
                  <SelectTrigger data-testid="fault-edit-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="P1">P1 kritiek</SelectItem>
                    <SelectItem value="P2">P2 hoog</SelectItem>
                    <SelectItem value="P3">P3 normaal</SelectItem>
                    <SelectItem value="P4">P4 laag</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Waar staat het?</Label>
                <Select value={editForm.ruimte_id} onValueChange={(v) => setEditForm((r) => ({ ...r, ruimte_id: v, spoor: "__none__" }))}>
                  <SelectTrigger data-testid="fault-edit-ruimte"><SelectValue placeholder="Weet ik niet" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Weet ik niet</SelectItem>
                    {faultRuimtes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Spoor</Label>
                <Select value={editForm.spoor} onValueChange={(v) => setEditForm((r) => ({ ...r, spoor: v }))} disabled={editSporen.length === 0}>
                  <SelectTrigger data-testid="fault-edit-spoor"><SelectValue placeholder={editSporen.length ? "Geen" : "Alleen in een was-hal"} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Geen</SelectItem>
                    {editSporen.map((sp) => <SelectItem key={sp} value={sp}>{sp}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Asset</Label>
                <Select value={editForm.asset_id} onValueChange={(v) => setEditForm((r) => ({ ...r, asset_id: v }))} disabled={!!f.repair_id}>
                  <SelectTrigger data-testid="fault-edit-asset"><SelectValue placeholder="Geen asset" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Geen asset</SelectItem>
                    {faultAssets.map((a) => <SelectItem key={a.id} value={a.id}>{[a.name || `${a.brand} ${a.model}`.trim(), a.spoor].filter(Boolean).join(" · ") || a.asset_code}</SelectItem>)}
                  </SelectContent>
                </Select>
                {f.repair_id ? (
                  <p className="text-data text-muted-foreground">Er is al een reparatie gekoppeld; het asset kan niet meer gewijzigd worden.</p>
                ) : (
                  <p className="text-data text-muted-foreground">Alleen assets op {f.location_name}. De wijziging wordt vastgelegd in de statusgeschiedenis.</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => editGuard.requestClose(false)}>Annuleren</Button>
            <Button data-testid="fault-edit-save" onClick={saveEdit} disabled={busy}>{busy ? "Bezig..." : "Opslaan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Koppelen aan bestaande werkbon (§2.2) */}
      <Dialog open={linkDlg} onOpenChange={setLinkDlg}>
        <DialogContent className="max-w-lg" srDescription="Kies een openstaande werkbon om deze storing aan te koppelen. Loskoppelen kan op de werkbon zelf.">
          <DialogHeader><DialogTitle>Koppelen aan bestaande werkbon</DialogTitle></DialogHeader>
          <p className="text-body text-muted-foreground">Alleen openstaande werkbonnen op {f?.location_name || "deze locatie"}.</p>
          <Input placeholder="Zoek op nummer of omschrijving" data-testid="fault-koppelen-search" value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} />
          <div className="max-h-[50vh] overflow-y-auto border border-lijn-zacht" data-testid="fault-koppelen-list">
            {linkLoading ? (
              <p className="p-3 text-body text-muted-foreground">Laden…</p>
            ) : linkableBonnen.length === 0 ? (
              <p className="p-3 text-body text-muted-foreground" data-testid="fault-koppelen-empty">Geen openstaande werkbon op deze locatie.</p>
            ) : (
              linkableBonnen
                .filter((b) => {
                  const q = linkSearch.trim().toLowerCase();
                  if (!q) return true;
                  return `${b.werkbon_number} ${b.omschrijving}`.toLowerCase().includes(q);
                })
                .map((b) => (
                  <button key={b.id} type="button" data-testid={`fault-koppelen-row-${b.id}`} className="block w-full border-b border-lijn-zacht p-3 text-left transition-colors hover:bg-accent" onClick={() => doLink(b.id)} disabled={busy}>
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-body font-medium text-foreground">{b.omschrijving}</div>
                      <span className="font-mono text-data">{b.werkbon_number}</span>
                    </div>
                    <div className="text-data text-muted-foreground">{[b.location_name, b.ruimte_name, b.spoor].filter(Boolean).join(" · ")}</div>
                  </button>
                ))
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setLinkDlg(false)}>Terug</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
